"use client";

import { cn, sectionBackgroundColors, variantBackgroundColors } from "@/lib/utils";
import React, { useRef } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import Link from "next/link";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SectionWrapper } from "../components/SectionWraper";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

interface GalleryItem {
  image?: string;
  imageAltText?: string;
  title?: string;
  content?: string;
  link?: string;
  tabTitle?: string;
  badges?: {
    badge?: string;
  }[];
}

interface Gallery3Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery3 = ({
  darkMode = false,
  variant = "A",
  spacing = "xlarge",
  usedInPageNav = false,
  pageNavTitle = "Gallery 3",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  galleryItems,
  attributes,
}: Gallery3Props) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(galleryItems?.[0]?.tabTitle);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    left: 0,
  });

  useEffect(() => {
    const currentIndex = galleryItems?.findIndex((item) => item.tabTitle === current);
    const activeTab = tabRefs.current[currentIndex ?? 0];

    if (activeTab) {
      const { offsetWidth, offsetLeft } = activeTab;
      setIndicatorStyle({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, [current, galleryItems]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateSelection = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    updateSelection();
    api.on("scroll", updateSelection);

    const currentIndex = galleryItems?.findIndex((item) => item.tabTitle === current);
    api.scrollTo(currentIndex ?? 0);

    const onSelect = () => {
      const idx = api.selectedScrollSnap();
      setCurrent(galleryItems?.[idx]?.tabTitle);
    };
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api, current, galleryItems]);

  const itemBackgroundClass = variantBackgroundColors(darkMode, variant);
  const badgeBackgroundClass = sectionBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery3"
      attributes={attributes}
      className="overflow-hidden"
    >   
      <div className="container" data-aos="fade-up">
        <div className="mb-8 md:mb-20 gap-20 flex flex-col justify-between items-center text-center">
          <div>
            {badge && (
              <Badge variant="outline" bold className="mb-4">
                {badge}
              </Badge>
            )}
            {title && (
              <h2 className="max-w-[500px] lg:max-w-[800px]">
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="mt-6 paragraph-lg text-muted-foreground">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <>
                <Button asChild variant={buttonStyle} size="lg" className="mt-10 w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>
                    {buttonText}
                  </Link>
                </Button>
                {microCopy && (
                  <div className="mt-2 paragraph-sm text-muted-foreground text-center sm:text-left">
                    {microCopy}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="relative container" data-aos="fade-up" data-aos-delay={100}>
        <Carousel
          setApi={setApi}
          className="[&>div[data-slot=carousel-content]]:overflow-visible"
        >
          <div className="items-center justify-between mb-10 hidden lg:flex">
            <Tabs
              value={current}
              onValueChange={setCurrent}
              className="flex justify-center"
            >
              <TabsList className="relative h-auto gap-0 bg-transparent">
                {galleryItems?.map((item, idx) => (
                  <TabsTrigger
                    key={idx}
                    ref={(el) => {
                      tabRefs.current[idx] = el;
                    }}
                    value={item.tabTitle ?? ""}
                    className="paragraph-base px-4 transition-all duration-700 ease-out [&[data-state=active]]:shadow-none [&[data-state=active]]:font-bold !bg-transparent cursor-pointer"
                  >
                    {item.tabTitle}
                  </TabsTrigger>
                ))}
                <div
                  className="absolute bottom-0 h-0.5 bg-red-500 transition-all duration-700 ease-out"
                  style={{
                    width: `${indicatorStyle.width}px`,
                    left: `${indicatorStyle.left}px`,
                  }}
                />
              </TabsList>
            </Tabs>
            <div className="items-center gap-4">
              <div className="mt-6 flex shrink-0 items-center justify-end gap-2 sm:mt-0">
                <Button
                  size="iconSm"
                  variant="outline"
                  grayBorder
                  onClick={() => {
                    api?.scrollPrev();
                  }}
                  disabled={!canScrollPrev}
                  className="disabled:pointer-events-auto"
                >
                  <span className="material-symbols-outlined">
                    arrow_back  
                  </span>
                </Button>
                <Button
                  size="iconSm"
                  variant="outline"
                  grayBorder
                  onClick={() => {
                    api?.scrollNext();
                  }}
                  disabled={!canScrollNext}
                  className="disabled:pointer-events-auto"
                >
                  <span className="material-symbols-outlined">
                    arrow_forward
                  </span>
                </Button>
              </div>
            </div>
          </div>
          <CarouselContent className="max-w-5xl">
            {galleryItems?.map((item, idx) => (
              <CarouselItem key={idx} className="w-fit max-w-5xl pl-6">
                {item.link ? (
                  <Link href={item.link ?? ""} tabIndex={-1}>
                    <div className={cn("grid h-full max-w-5xl rounded-xl md:max-h-[480px] md:grid-cols-9", itemBackgroundClass)}>
                      <div className="flex flex-col justify-between gap-10 p-6 lg:p-10 md:col-span-5">
                        <div className="flex flex-col gap-6">
                          <Badge variant="outline" bold>
                            {item.tabTitle}
                          </Badge>
                          <h4>
                            {parse(item.title ?? "")}
                          </h4>
                          <div className="paragraph-base text-muted-foreground">
                            {parse(item.content ?? "")}
                          </div>
                        </div>
                        <div className="flex flex-row gap-6 justify-between flex-wrap">
                          <div className="flex flex-col gap-2">
                            {item.badges?.map((badge, idx) => (
                              <Badge key={idx} variant="secondary" bold wrap className={badgeBackgroundClass}>
                                {badge.badge}
                              </Badge>
                            ))}
                          </div>
                          {item.link && (
                            <Button variant="outline" size="iconLg" grayBorder className='self-end flex'>
                              <span className="material-symbols-outlined text-foreground group-hover/gallery2:text-primary-foreground">
                                arrow_outward
                              </span>                      
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="rounded-r-xl md:col-span-4 hidden md:block">
                        <Image
                          src={item.image ?? ""}
                          alt={item.imageAltText ?? ""}
                          title={item.imageAltText ?? ""}
                          width={436}
                          height={480}
                          className="h-full w-full rounded-b-xl md:rounded-l-none md:rounded-r-xl object-cover"
                        />
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className={cn("grid h-full max-w-5xl rounded-xl md:max-h-[480px] md:grid-cols-9", itemBackgroundClass)}>
                    <div className="flex flex-col justify-between gap-10 p-6 lg:p-10 md:col-span-5">
                      <div className="flex flex-col gap-6">
                        <Badge variant="outline" bold>
                          {item.tabTitle}
                        </Badge>
                        <h4>
                          {parse(item.title ?? "")}
                        </h4>
                        <div className="paragraph-base text-muted-foreground">
                          {parse(item.content ?? "")}
                        </div>
                      </div>
                      <div className="flex flex-row gap-6 justify-between flex-wrap">
                        <div className="flex flex-col gap-2">
                          {item.badges?.map((badge, idx) => (
                            <Badge key={idx} variant="secondary" bold wrap className={badgeBackgroundClass}>
                              {badge.badge}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="rounded-r-xl md:col-span-4 hidden lg:block">
                      <Image
                        src={item.image ?? ""}
                        alt={item.imageAltText ?? ""}
                        title={item.imageAltText ?? ""}
                        width={436}
                        height={480}
                        className="h-full w-full rounded-b-xl md:rounded-l-none md:rounded-r-xl object-cover"
                      />
                    </div>
                  </div>
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

          <div className="mt-8 flex shrink-0 items-center justify-center md:justify-end gap-2 lg:hidden">
            <Button
              size="iconSm"
              variant="outline"
              grayBorder
              onClick={() => {
                api?.scrollPrev();
              }}
              disabled={!canScrollPrev}
              className="disabled:pointer-events-auto"
            >
              <span className="material-symbols-outlined">
                arrow_back  
              </span>
            </Button>
            <Button
              size="iconSm"
              variant="outline"
              grayBorder
              onClick={() => {
                api?.scrollNext();
              }}
              disabled={!canScrollNext}
              className="disabled:pointer-events-auto"
            >
              <span className="material-symbols-outlined">
                arrow_forward
              </span>
            </Button>
          </div>

      </div>
    </SectionWrapper>
  );
};

export { Gallery3 };
